"use client";

import { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Metric = {
  label: string;
  value: number;
  unit: string;
  descriptor: string;
  decimals: number;
};
const metrics: Metric[] = [{
  label: "COLOR ACCURACY",
  value: 99,
  unit: "%",
  descriptor: "Precision color matching for vibrant, high-fidelity apparel transfers.",
  decimals: 0
}, {
  label: "WASH DURABILITY",
  value: 50,
  unit: "+",
  descriptor: "Guaranteed wash cycles without cracking, fading, or peeling of prints.",
  decimals: 0
}, {
  label: "TURNAROUND TIME",
  value: 24,
  unit: "hrs",
  descriptor: "Rapid production cycles to keep your fashion brand moving at speed.",
  decimals: 0
}, {
  label: "INK EFFICIENCY",
  value: 100,
  unit: "%",
  descriptor: "Eco-friendly, water-based ink systems with zero hazardous waste output.",
  decimals: 0
}];
function CountUp({
  end,
  decimals,
  isVisible
}: {
  end: number;
  decimals: number;
  isVisible: boolean;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const duration = 2000;
    const startTime = performance.now();
    let rafId: number;
    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(end * eased);
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        setCount(end);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [end, isVisible]);
  return <>{count.toFixed(decimals)}</>;
}
export default function Standards() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  return <section data-section-id="3601" id="our-printing-standards" className="relative bg-background text-foreground overflow-hidden py-[180px] px-4 sm:px-6 lg:px-8">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-20" style={{
      backgroundImage: "linear-gradient(to right, var(--border) 1px, transparent 1px), linear-gradient(to bottom, var(--border) 1px, transparent 1px)",
      backgroundSize: "100px 100px"
    }} />

      <div ref={ref} className="relative max-w-7xl mx-auto">
        {}
        <div className="text-center mb-8">
          <span className={`inline-block font-mono text-xs sm:text-sm uppercase tracking-[0.2em] text-secondary transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            § 02 / TECHNICAL STANDARDS
          </span>
        </div>

        {}
        <h2 className={`font-default font-bold text-center uppercase tracking-tight leading-[0.95] mb-20 sm:mb-24 lg:mb-28 transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        fontSize: "clamp(2.5rem, 6vw, 4rem)"
      }}>
          <span className="text-foreground">THE DTF </span>
          <span className="text-primary">ADVANTAGE</span>
        </h2>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-b border-border">
          {metrics.map((metric, i) => <div key={metric.label} data-index={i} className={`relative p-8 lg:p-10 flex flex-col min-h-[320px] md:min-h-[400px] transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${i < metrics.length - 1 ? "border-b md:border-b-0 lg:border-r border-border" : ""} ${i === 1 ? "md:border-b lg:border-b-0 border-border" : ""} ${i < 2 ? "md:border-r lg:border-r border-border" : ""}`} style={{
          transitionDelay: `${200 + i * 100}ms`
        }}>
              {}
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-secondary mb-auto">
                {metric.label}
              </div>

              {}
              <div className="flex items-baseline gap-2 my-8">
                <span className="font-default font-bold text-foreground leading-none tracking-tight" style={{
              fontSize: "clamp(5rem, 8vw, 8rem)"
            }}>
                  <CountUp end={metric.value} decimals={metric.decimals} isVisible={isVisible} />
                </span>
                <span className="font-default font-bold text-primary leading-none" style={{
              fontSize: "clamp(2rem, 3vw, 3rem)"
            }}>
                  {metric.unit}
                </span>
              </div>

              {}
              <p className="font-sans text-base lg:text-lg text-secondary leading-relaxed">
                {metric.descriptor}
              </p>
            </div>)}
        </div>

        {}
        <div className={`border-b border-border transition-all duration-700 delay-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 py-6 px-2">
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-secondary text-center">
              OEKO-TEX CERTIFIED
            </div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-secondary text-center">
              INDUSTRIAL GRADE
            </div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-secondary text-center">
              ISO 9001 QUALITY
            </div>
          </div>
        </div>
      </div>
    </section>;
}